import { apiSlice } from './apiSlice'

/** Loại chứng cứ — enum 5 loại theo BR-03-02 (đội tự gán nhãn) */
export type EvidenceCategory =
  | 'text'
  | 'image'
  | 'audio'
  | 'video'
  | 'spreadsheet'
  | 'other'

export interface Evidence {
  id: string
  case_id: string
  team_id: string
  title: string
  description?: string
  file_url: string
  file_name: string
  file_size: number
  mime_type: string
  category?: EvidenceCategory | string
  tags: string[]
  uploaded_at?: string
  uploaded_by: string
  storage_path?: string
  is_public?: boolean
  public_at?: string | null
  public_by?: string | null
  public_to_team_ids?: string[]
  /** Có thể trả từ BE (nếu evidence đã từng nộp chính thức) */
  submitted_at?: string | null
  status?: string
  /** Đội biện giải vì sao chọn chứng cứ này (BR-03-01) — 30-500 ký tự */
  justification?: string | null
  /** Bookmark đoạn Memorial mà chứng cứ này hỗ trợ */
  linked_memorial_section?: string | null
  /** Trang trích dẫn (cho PDF nhiều trang) */
  cited_pages?: number[]
  /** Flow `dual_claimant_packets`: side của evidence — 'scenario_a' | 'scenario_b' */
  r1_side?: 'scenario_a' | 'scenario_b' | null
  /** Q1 audit (2026-05-21): alias FE-friendly của r1_side — 1 = scenario_a, 2 = scenario_b */
  submission_index?: 1 | 2 | null
}

export interface PublicizeCaseRequest {
  case_id: string
  team_ids?: string[]
}

export interface EvidencePoolItem {
  document_id: string
  case_id: string
  title: string
  description?: string
  file_name: string
  file_size: number
  mime_type: string
  document_type: string
}

export interface EvidenceAiSuggestionsResponse {
  case_id: string
  suggestions: { id: string; title: string; detail: string; source_document_id?: string }[]
  notice: string
}

/** Đồng bộ với GET /evidence/deadline-status */
export interface EvidenceDeadlineStatus {
  case_id: string
  submission_open: boolean
  message: string
  case_deadline?: string | null
  round_evidence_deadline?: string | null
  round_id?: string
  round_opens_at?: string | null
  round_closes_at?: string | null
  server_now_iso?: string | null
  timezone?: string
}

/** Đồng bộ với GET /evidence/lock-status — khóa nộp chính thức theo (case, team) */
export interface EvidenceSubmissionLockState {
  locked: boolean
  case_id: string
  team_id: string
  total_items?: number | null
  locked_at?: string | null
  locked_by_user_id?: string | null
  r1_side?: 'scenario_a' | 'scenario_b' | null
}

export interface EvidenceCreate {
  case_id: string
  team_id: string
  title: string
  description?: string
  category?: string
  tags?: string[]
  file: File
}

export const evidenceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEvidence: builder.query<
      Evidence[],
      { case_id?: string; team_id?: string; r1_side?: 'scenario_a' | 'scenario_b' }
    >({
      query: (params) => ({
        url: '/evidence/list',
        params,
      }),
      providesTags: ['Evidence'],
    }),
    getEvidenceDeadlineStatus: builder.query<EvidenceDeadlineStatus, string>({
      query: (case_id) => ({
        url: '/evidence/deadline-status',
        params: { case_id },
      }),
    }),
    getEvidencePool: builder.query<EvidencePoolItem[], string>({
      query: (case_id) => ({
        url: '/evidence/pool',
        params: { case_id },
      }),
      providesTags: ['Evidence'],
    }),
    createEvidenceFromPool: builder.mutation<
      Evidence,
      {
        case_id: string
        team_id: string
        document_id: string
        title?: string
        category?: EvidenceCategory
        justification?: string
        linked_memorial_section?: string
        cited_pages?: number[]
        r1_side?: 'scenario_a' | 'scenario_b'
      }
    >({
      query: (body) => ({
        url: '/evidence/from-pool',
        method: 'POST',
        body,
      }),
      // TRUE optimistic update — push placeholder NGAY trước khi gọi BE,
      // UI cập nhật instant. Sau khi BE response → replace bằng real evidence.
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const tempId = `__optimistic_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
        const nowIso = new Date().toISOString()
        // Tìm document title từ pool cache nếu có để hiện tên đẹp hơn
        const state: any = getState()
        const poolEntries = state[evidenceApi.reducerPath]?.queries || {}
        let poolTitle = arg.title || 'Đang xử lý...'
        let poolFileName = ''
        let poolMime = 'application/octet-stream'
        for (const k of Object.keys(poolEntries)) {
          if (!k.startsWith('getEvidencePool(')) continue
          const items = poolEntries[k]?.data
          if (!Array.isArray(items)) continue
          const found = items.find((p: any) => p.document_id === arg.document_id)
          if (found) {
            poolTitle = found.title || poolTitle
            poolFileName = found.file_name || ''
            poolMime = found.mime_type || poolMime
            break
          }
        }
        const tempEvidence: any = {
          id: tempId,
          case_id: arg.case_id,
          team_id: arg.team_id,
          title: poolTitle,
          file_name: poolFileName,
          file_size: 0,
          mime_type: poolMime,
          status: 'submitted',
          tags: [`from_pool_document:${arg.document_id}`],
          uploaded_at: nowIso,
          submitted_at: nowIso,
          uploaded_by: '',
          storage_path: '',
          file_url: '',
          is_public: false,
        }

        const patches: { undo: () => void; replaceTemp: (real: any) => void }[] = []
        const queries = state[evidenceApi.reducerPath]?.queries || {}
        for (const key of Object.keys(queries)) {
          if (!key.startsWith('getEvidence(')) continue
          try {
            const args = JSON.parse(key.slice('getEvidence('.length, -1))
            // Chỉ patch list khớp case_id (hoặc list "tất cả")
            if (args && 'case_id' in args && args.case_id && args.case_id !== arg.case_id) continue
            if (args && 'team_id' in args && args.team_id && args.team_id !== arg.team_id) continue
            const undoer = dispatch(
              evidenceApi.util.updateQueryData('getEvidence', args, (draft: Evidence[]) => {
                draft.unshift(tempEvidence)
              }),
            )
            patches.push({
              undo: () => undoer.undo?.(),
              replaceTemp: (real: any) => {
                dispatch(
                  evidenceApi.util.updateQueryData('getEvidence', args, (draft: Evidence[]) => {
                    const idx = draft.findIndex((e) => e.id === tempId)
                    if (idx !== -1) draft[idx] = real
                    else if (!draft.find((e) => e.id === real.id)) draft.unshift(real)
                  }),
                )
              },
            })
          } catch {}
        }

        try {
          const { data: created } = await queryFulfilled
          if (created) {
            // Ép submitted_at giữ ≥ placeholder timestamp — tránh real bị sort nhảy
            // xuống dưới placeholder vì BE clock < client clock (clock skew).
            const stableSubmittedAt =
              created.submitted_at && created.submitted_at > nowIso
                ? created.submitted_at
                : nowIso
            const stableUploadedAt =
              created.uploaded_at && created.uploaded_at > nowIso
                ? created.uploaded_at
                : nowIso
            patches.forEach((p) =>
              p.replaceTemp({
                ...created,
                submitted_at: stableSubmittedAt,
                uploaded_at: stableUploadedAt,
              }),
            )
          }
        } catch {
          patches.forEach((p) => p.undo())
        }
      },
      // KHÔNG invalidate — optimistic patch + replaceTemp đã sync cache đầy đủ.
      // Nếu invalidate, getEvidence refetch sẽ replace cache → row nhảy vị trí
      // theo thứ tự BE trả về rồi useMemo sort lại → flash UI tệ.
    }),
    getEvidenceAiSuggestions: builder.query<
      EvidenceAiSuggestionsResponse,
      { case_id: string; q?: string }
    >({
      query: ({ case_id, q }) => ({
        url: '/evidence/ai-suggestions',
        params: {
          case_id,
          ...(q && q.trim() ? { q: q.trim() } : {}),
        },
      }),
    }),
    getEvidenceById: builder.query<Evidence, string>({
      query: (id) => `/evidence/${id}`,
      providesTags: (result, error, id) => [{ type: 'Evidence', id }],
    }),
    uploadEvidence: builder.mutation<Evidence, FormData>({
      query: (formData) => ({
        url: '/evidence/create',
        method: 'POST',
        body: formData,
      }),
      // Optimistic patch — push evidence mới vào list cache ngay, không chờ refetch
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled
          if (!created) return
          // Patch list query cho đúng case_id (FE thường query theo case)
          dispatch(
            evidenceApi.util.updateQueryData(
              'getEvidence',
              { case_id: created.case_id || undefined },
              (draft: Evidence[]) => {
                if (!draft.find((e) => e.id === created.id)) draft.unshift(created)
              },
            ),
          )
          // Patch list "tất cả" (no filter)
          dispatch(
            evidenceApi.util.updateQueryData('getEvidence', {}, (draft: Evidence[]) => {
              if (!draft.find((e) => e.id === created.id)) draft.unshift(created)
            }),
          )
        } catch {}
      },
      invalidatesTags: ['Evidence'],
    }),
    updateEvidenceMetadata: builder.mutation<
      Evidence,
      { id: string; category?: string | null; description?: string | null; title?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/evidence/${id}`,
        method: 'PATCH',
        body,
      }),
      // Optimistic patch — update list + single cache ngay
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled, getState }) {
        const state: any = getState()
        const queries = state[evidenceApi.reducerPath]?.queries || {}
        const undoers: { undo: () => void }[] = []
        for (const key of Object.keys(queries)) {
          if (!key.startsWith('getEvidence(')) continue
          try {
            const args = JSON.parse(key.slice('getEvidence('.length, -1))
            const u = dispatch(
              evidenceApi.util.updateQueryData('getEvidence', args, (draft: Evidence[]) => {
                const e = draft.find((x) => x.id === id)
                if (e) Object.assign(e, patch)
              }),
            )
            undoers.push(u as any)
          } catch {}
        }
        try {
          await queryFulfilled
        } catch {
          undoers.forEach((u) => u.undo?.())
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Evidence', id }, 'Evidence'],
    }),
    deleteEvidence: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/evidence/${id}`,
        method: 'DELETE',
      }),
      // Optimistic remove — xóa khỏi cache ngay, không chờ refetch
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const state: any = getState()
        const queries = state[evidenceApi.reducerPath]?.queries || {}
        const patches: { undo: () => void }[] = []
        for (const key of Object.keys(queries)) {
          if (!key.startsWith('getEvidence(')) continue
          try {
            const args = JSON.parse(key.slice('getEvidence('.length, -1))
            const patch = dispatch(
              evidenceApi.util.updateQueryData('getEvidence', args, (draft: Evidence[]) => {
                const idx = draft.findIndex((e) => e.id === id)
                if (idx !== -1) draft.splice(idx, 1)
              }),
            )
            patches.push(patch as any)
          } catch {}
        }
        try {
          await queryFulfilled
        } catch (err: any) {
          // Nếu BE trả 404 (item đã bị xóa rồi — vd. user double-click) → giữ optimistic patch.
          // Chỉ undo khi lỗi thật (network/500/403)
          const status = err?.error?.status ?? err?.status
          if (status === 404) return
          patches.forEach((p) => p.undo?.())
        }
      },
      invalidatesTags: (result, error, id) => [{ type: 'Evidence', id }],
    }),
    publicizeCaseEvidence: builder.mutation<{ message: string; updated: number }, PublicizeCaseRequest>({
      query: (body) => ({
        url: '/evidence/case/publicize',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Evidence'],
    }),
    downloadEvidence: builder.query<Blob, string>({
      query: (id) => ({
        url: `/evidence/${id}/download`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    downloadEvidenceAsPdf: builder.query<Blob, string>({
      query: (id) => ({
        url: `/evidence/${id}/download?as_pdf=1`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    getEvidenceLockStatus: builder.query<
      EvidenceSubmissionLockState,
      { case_id: string; team_id: string; r1_side?: 'scenario_a' | 'scenario_b' }
    >({
      query: ({ case_id, team_id, r1_side }) => ({
        url: '/evidence/lock-status',
        params: { case_id, team_id, ...(r1_side ? { r1_side } : {}) },
      }),
      providesTags: (_r, _e, { case_id, team_id, r1_side }) => [
        {
          type: 'Evidence',
          id: `lock-${case_id}-${team_id}${r1_side ? `-${r1_side}` : ''}`,
        },
      ],
    }),
    getEvidenceLocksForCase: builder.query<EvidenceSubmissionLockState[], string>({
      query: (case_id) => ({
        url: '/evidence/locks',
        params: { case_id },
      }),
      providesTags: (_r, _e, case_id) => [{ type: 'Evidence', id: `locks-${case_id}` }],
    }),
    officialSubmitEvidence: builder.mutation<
      EvidenceSubmissionLockState,
      { case_id: string; team_id: string; r1_side?: 'scenario_a' | 'scenario_b' }
    >({
      query: (body) => ({
        url: '/evidence/official-submit',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { case_id, team_id, r1_side }) => [
        {
          type: 'Evidence',
          id: `lock-${case_id}-${team_id}${r1_side ? `-${r1_side}` : ''}`,
        },
        { type: 'Evidence', id: `locks-${case_id}` },
        'Evidence',
        { type: 'R1Packet' as const, id: `${case_id}:${team_id}` },
        { type: 'R1Packet' as const, id: `case:${case_id}` },
      ],
    }),
    releaseEvidenceLock: builder.mutation<
      { message: string },
      {
        case_id: string
        team_id: string
        reason?: string
        r1_side?: 'scenario_a' | 'scenario_b'
      }
    >({
      query: ({ case_id, team_id, reason, r1_side }) => ({
        url: '/evidence/admin/submission-lock',
        method: 'DELETE',
        params: {
          case_id,
          team_id,
          ...(reason ? { reason } : {}),
          ...(r1_side ? { r1_side } : {}),
        },
      }),
      invalidatesTags: (_r, _e, { case_id, team_id, r1_side }) => [
        {
          type: 'Evidence',
          id: `lock-${case_id}-${team_id}${r1_side ? `-${r1_side}` : ''}`,
        },
        { type: 'Evidence', id: `locks-${case_id}` },
        'Evidence',
        { type: 'R1Packet' as const, id: `${case_id}:${team_id}` },
        { type: 'R1Packet' as const, id: `case:${case_id}` },
      ],
    }),
  }),
})

export const {
  useGetEvidenceQuery,
  useGetEvidenceDeadlineStatusQuery,
  useGetEvidenceByIdQuery,
  useGetEvidencePoolQuery,
  useCreateEvidenceFromPoolMutation,
  useGetEvidenceAiSuggestionsQuery,
  useLazyGetEvidenceAiSuggestionsQuery,
  useUploadEvidenceMutation,
  useDeleteEvidenceMutation,
  useUpdateEvidenceMetadataMutation,
  useLazyDownloadEvidenceQuery,
  useLazyDownloadEvidenceAsPdfQuery,
  usePublicizeCaseEvidenceMutation,
  useGetEvidenceLockStatusQuery,
  useGetEvidenceLocksForCaseQuery,
  useOfficialSubmitEvidenceMutation,
  useReleaseEvidenceLockMutation,
} = evidenceApi
